import { useEffect, useState, useRef } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router';
import { Mail, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from './AuthContext';
import { authService } from './services/authService';
import { EMAIL_VERIFY_REDIRECT_DELAY_MS } from './config/constants';
import AuthLayout from './components/auth/AuthLayout';
import AuthCard from './components/auth/AuthCard';
import Button from './components/ui/Button';

function CheckEmail() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [status, setStatus] = useState('waiting');
  const [message, setMessage] = useState('');
  const verifying = useRef(false);
  const resendIntervalRef = useRef(null);
  const redirectTimeoutRef = useRef(null);
  const [email, setEmail] = useState(() => {
    const stateEmail = location.state?.email?.trim()?.toLowerCase();
    const queryEmail = searchParams.get('email')?.trim()?.toLowerCase();
    return stateEmail || queryEmail || '';
  });

  const [resendState, setResendState] = useState('idle'); // 'idle' | 'sending' | 'sent'
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    const stateEmail = location.state?.email?.trim()?.toLowerCase();
    const queryEmail = searchParams.get('email')?.trim()?.toLowerCase();
    const nextEmail = stateEmail || queryEmail || '';

    if (nextEmail) {
      setEmail(nextEmail);
    }
  }, [location.state?.email, searchParams]);

  useEffect(() => {
    return () => {
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
      }
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  const handleResend = async () => {
    if (resendState !== 'idle' || resendCooldown > 0 || !email) return;

    setResendState('sending');
    try {
      await authService.resendSignupLink(email);
    } catch {
      // Fail silently — don't reveal whether the email exists
    }

    setResendState('sent');
    setResendCooldown(60);

    if (resendIntervalRef.current) {
      clearInterval(resendIntervalRef.current);
    }

    resendIntervalRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(resendIntervalRef.current);
          resendIntervalRef.current = null;
          setResendState('idle');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    const token = searchParams.get('token');
    if (token && !verifying.current) {
      verifying.current = true;
      verifyToken(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const verifyToken = async (token) => {
    setStatus('verifying');

    try {
      const data = await authService.verifyEmailToken(token);

      login(
        { accessToken: data.accessToken, refreshToken: data.refreshToken || null },
        data.user,
        data.household,
        data.onboarding,
        data.billing || null,
        data.features || {}
      );

      setStatus('success');
      setMessage('Email verified! Redirecting...');

      if (!data.onboarding?.completed) {
        import('./Onboarding').catch(() => {});
      }

      redirectTimeoutRef.current = setTimeout(() => {
        if (!data.onboarding?.completed) {
          const step = data.onboarding?.currentStep || 1;
          navigate(`/onboarding?step=${step}`);
        } else {
          navigate('/dashboard');
        }
      }, EMAIL_VERIFY_REDIRECT_DELAY_MS);
    } catch (error) {
      setStatus('error');
      setMessage(error.message || 'Something went wrong.');
    }
  };

  return (
    <AuthLayout variant="status"><AuthCard variant="status">
          {status === 'waiting' && (
            <div className="auth-status-content">
              <div className="auth-status-icon">
                <Mail className="h-7 w-7 text-[var(--color-text-primary)]" />
              </div>
              <h2 className="mb-2 text-2xl font-bold text-[var(--color-text-primary)]">Verify your email</h2>
              <p className="auth-status-message text-[var(--color-text-secondary)]">
                We sent a sign-in link to{' '}
                <span className="font-medium text-[var(--color-text-primary)]">{email || 'your email'}</span>
              </p>
              <div className="auth-status-actions">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleResend}
                  disabled={resendState === 'sending' || resendCooldown > 0 || !email}
                >
                  {resendState === 'sending' && 'Sending…'}
                  {resendState === 'sent' && resendCooldown > 0 && `Sent · resend in ${resendCooldown}s`}
                  {resendState === 'idle' && 'Resend'}
                </Button>
                {!email && (
                  <p className="text-xs text-[var(--color-text-muted)]">
                    Resend is only available after starting signup from this device.
                  </p>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/login')}
                >
                  Back to login
                </Button>
              </div>
            </div>
          )}

          {status === 'verifying' && (
            <div className="auth-status-content">
              <div className="auth-status-icon auth-status-icon--loading"><Loader2 className="animate-spin" aria-hidden="true" /></div>
              <h2 className="mb-2 text-2xl font-bold text-[var(--color-text-primary)]">Verifying...</h2>
            </div>
          )}

          {status === 'success' && (
            <div className="auth-status-content">
              <div className="auth-status-icon success">
                <CheckCircle className="h-10 w-10 text-[var(--color-text-primary)]" />
              </div>
              <h2 className="mb-2 text-2xl font-bold text-[var(--color-text-primary)]">Verified!</h2>
              <p className="auth-status-message text-[var(--color-text-secondary)]">{message}</p>
            </div>
          )}

          {status === 'error' && (
            <div className="auth-status-content">
              <div className="auth-status-icon failure">
                <XCircle className="h-10 w-10 text-[var(--color-text-primary)]" />
              </div>
              <h2 className="mb-2 text-2xl font-bold text-[var(--color-text-primary)]">Failed</h2>
              <p className="auth-status-message text-[var(--color-text-secondary)]">{message}</p>
              <div className="auth-status-actions">
                <Button type="button" onClick={() => navigate('/login')}>
                  Back to Login
                </Button>
              </div>
            </div>
          )}
        </AuthCard></AuthLayout>
  );
}

export default CheckEmail;
